import type { QueryClient } from '@tanstack/react-query';
import type { ListResponse, TicketSummary } from '@aurora/contracts';

/**
 * Where ticket data lives in the query cache.
 *
 * Every list, count and dashboard panel sits under the one `['tickets']` root,
 * so a single invalidation refreshes them all. They used to live under keys of
 * their own — `['ticket-count', …]`, `['dashboard-panel', …]`,
 * `['requester-home', …]` — and a status changed on the list left the
 * dashboard counting the old one until its cache happened to expire.
 *
 * Saved views are deliberately *not* under the root: changing a ticket does not
 * change the list of views, and refetching it on every edit would be waste.
 */
export const ticketKeys = {
  all: ['tickets'] as const,
  list: (params: unknown) => ['tickets', 'list', params] as const,
  count: (id: string, params: unknown) => ['tickets', 'count', id, params] as const,
  panel: (id: string, params?: unknown) => ['tickets', 'panel', id, params ?? null] as const,
  workload: ['tickets', 'workload'] as const,
};

export const savedViewsKey = ['saved-views', 'tickets'] as const;

/**
 * Refreshes every ticket list, count and panel, and the given tickets' own
 * pages. The ticket page keys its queries by the number as it appears in the
 * URL — a string — so that is what is invalidated here.
 */
export function invalidateTicket(queryClient: QueryClient, numbers?: number | number[]): void {
  void queryClient.invalidateQueries({ queryKey: ticketKeys.all });
  const list = numbers === undefined ? [] : Array.isArray(numbers) ? numbers : [numbers];
  for (const number of list) {
    void queryClient.invalidateQueries({ queryKey: ['ticket', String(number)] });
    void queryClient.invalidateQueries({ queryKey: ['ticket-timeline', String(number)] });
  }
}

function isTicketPage(value: unknown): value is ListResponse<TicketSummary> {
  return (
    !!value &&
    typeof value === 'object' &&
    Array.isArray((value as { data?: unknown }).data) &&
    typeof (value as { meta?: unknown }).meta === 'object'
  );
}

/**
 * Puts the server's copy of a ticket into every cached page that shows it, so
 * a change made in place is right immediately — not after a refetch, and not
 * flickering back to the old value while one is in flight.
 */
export function patchTicketInLists(queryClient: QueryClient, ticket: TicketSummary): void {
  // The update endpoint answers with the ticket's full detail; a list row has
  // no use for the description, and keeping it there would bloat every page.
  const {
    description_html: _html,
    description_text: _text,
    ...summary
  } = ticket as TicketSummary & { description_html?: unknown; description_text?: unknown };
  queryClient.setQueriesData({ queryKey: ticketKeys.all }, (data: unknown) => {
    if (!isTicketPage(data)) return data;
    if (!data.data.some((row) => row.number === ticket.number)) return data;
    return {
      ...data,
      data: data.data.map((row) => (row.number === ticket.number ? { ...row, ...summary } : row)),
    };
  });
}
